import { useCallback, useRef, useState } from 'react';

type Updater<T> = T | ((currentValue: T) => T);

/**
 * This is useful when you need asynchronous access to the latest value of a ref,
 * but also want your UI to re-render when the ref is updated.
 */
export const useStatefulRef = <T>(
  initialValue: T
): [() => T, (updater: Updater<T>) => void] => {
  const [, setValue] = useState<T>(initialValue);
  const ref = useRef<T>(initialValue);

  const setRef = useCallback((updater: Updater<T>) => {
    // Type assertion used here to handle the case where the value we are storing is a function.
    // In this case, calling updater(ref.current) will behave oddly. React setState has some similar
    // issues around setState with functions being stored as state.
    //
    // You can still update the value correctly in this case, but in order to do so, you will need to
    // pass a wrapped function like so: `update((__currentValue) => () => undefined)`
    ref.current =
      typeof updater === 'function'
        ? (updater as (current: T) => T)(ref.current)
        : updater;
    setValue(ref.current);
  }, []);

  const getRef = useCallback(() => ref.current, []);

  return [getRef, setRef];
};
